/**
 * Header component with authentication controls
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const { user, isAuthenticated, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut();
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Sign out failed:', error);
      // Handle error (could show a toast notification)
    } finally {
      setIsSigningOut(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  if (!isAuthenticated || !user) {
    return null; // Don't render header if not authenticated
  }

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.left}>
          <h1 className={styles.title}>LanguageApp Admin</h1>
        </div>
        
        <div className={styles.right}>
          <div className={styles.userSection}>
            <button 
              className={styles.userButton}
              onClick={toggleMenu}
              aria-label="User menu"
            >
              {user.photoURL ? (
                <img 
                  src={user.photoURL} 
                  alt={user.displayName || 'User'} 
                  className={styles.userAvatar}
                />
              ) : (
                <div className={styles.userAvatarPlaceholder}>
                  {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                </div>
              )}
              <span className={styles.userName}>
                {user.displayName || user.email}
              </span>
              {isAdmin && (
                <button
                  className={styles.adminBadge}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/admin/users');
                  }}
                  title="User Management"
                >
                  Admin
                </button>
              )}
              <svg
                className={`${styles.chevron} ${isMenuOpen ? styles.chevronRotated : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {isMenuOpen && (
              <>
                <div className={styles.overlay} onClick={closeMenu} />
                <div className={styles.dropdown}>
                  <div className={styles.userInfo}>
                    <div className={styles.userDisplayName}>
                      {user.displayName || 'User'}
                    </div>
                    <div className={styles.userEmail}>
                      {user.email}
                    </div>
                  </div>
                  <hr className={styles.divider} />
                  <button
                    className={styles.signOutButton}
                    onClick={handleSignOut}
                    disabled={isSigningOut}
                  >
                    {isSigningOut ? (
                      <>
                        <div className={styles.spinner}></div>
                        <span>Signing out...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>Sign Out</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;